import cx from 'classnames'
import { useState, useEffect } from 'react'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import { useMachine } from '@xstate/react'

import Styles from './UpgradePlanModal.module.scss'

import { workspacesSet } from 'redux/actions'
import { upgradePlanMachine } from './upgradePlanMachine'
import { Workspace } from 'types'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { SelectPlan } from './components/SelectPlan/SelectPlan'
import { ProvideReason } from './components/ProvideReason/ProvideReason'
import { CancelSuccess } from './components/CancelSuccess/CancelSuccess'
import { PaymentForm } from './components/PaymentForm/PaymentForm'
import { type PlanDetails } from 'api/clients/WorkspaceClient'
import { PaymentPlan } from 'api/clients/PaymentPlanClient'
import { FREE_PLAN, usePaymentPlans } from 'hooks/usePaymentPlans'
import { Modal, useModals } from 'almanac-ui/Modal'
import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { Dayjs } from 'dayjs'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { AutopilotUpgradeModal } from 'components/AutopilotUpgradeModal/AutopilotUpgradeModal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

export type BillingFrequency = 'year' | 'month'

interface Props {
  onClose: () => void
  onUpgradePlanSuccess?: (message?: string) => void
  onSuccess?: (paying: boolean, plan_details?: PlanDetails) => void
  showSuccessModal: boolean
  initialPage?: string
  initialSavedPlan?: PaymentPlan
  initialSelectedPlan?: PaymentPlan
  initialBillingFrequency?: BillingFrequency
  showWorkspacesLink?: boolean
  isCanceled?: boolean
}

export const UpgradePlanModal = ({
  onClose,
  onUpgradePlanSuccess,
  onSuccess,
  showSuccessModal,
  initialSavedPlan,
  initialSelectedPlan,
  initialBillingFrequency,
  initialPage = 'selectPlan',
  showWorkspacesLink = false,
  isCanceled,
}: Props) => {
  const [state, send] = useMachine(upgradePlanMachine({ initial: initialPage }))
  const { fetched: paymentPlansFetched, paymentPlans } = usePaymentPlans()
  const [billingFrequency, setBillingFrequency] = useState<BillingFrequency>(initialBillingFrequency || 'year')
  const [selectedPlan, setSelectedPlan] = useState<PaymentPlan | undefined>(initialSelectedPlan)
  const [savedPlan, setSavedPlan] = useState(initialSavedPlan || FREE_PLAN)
  const [subscriptionPeriodEndsAt, setSubscriptionPeriodEndsAt] = useState<Dayjs | null>(null)
  const dispatch = useDispatch()
  const workspaces = useWorkspaces()
  const workspace = useCurrentWorkspace()
  const { isFetched: currentPlanFetched, data: currentPlan } = useCurrentPlan()
  const { openModal } = useModals()
  const { isMobile } = useResponsiveDesign()

  const updateWorkspace = (updateFields: any) => {
    const updatedWorkspaces = workspaces.map((ws) => {
      if (ws.id === workspace?.id) {
        return { ...workspace, ...updateFields } as Workspace
      }

      return ws
    })
    dispatch(workspacesSet(updatedWorkspaces))
  }

  const updateWorkspacePaying = (paying: boolean) => {
    if (!paying) return

    updateWorkspace({ paying })
  }

  const startDate = () => {
    return moment().format('ll')
  }

  const selectPlan = (plan: PaymentPlan) => {
    setSelectedPlan(plan)
    if (plan === FREE_PLAN) {
      send('PROVIDE_REASON')
    } else {
      send('CONTINUE_TO_PAYMENT')
    }
  }

  useEffect(() => {
    if (paymentPlansFetched && !selectedPlan) {
      setSelectedPlan(paymentPlans[0])
    }
  }, [setSelectedPlan, paymentPlansFetched, selectedPlan, paymentPlans])

  useEffect(() => {
    if (currentPlanFetched && paymentPlansFetched) {
      const plan = paymentPlans.find((p) => p.key === currentPlan?.plan)
      plan && setSavedPlan(plan)
    }
  }, [currentPlanFetched, paymentPlansFetched, currentPlan, paymentPlans])

  useEffect(() => {
    if (state.matches('paymentSuccess')) {
      if (showSuccessModal) {
        send('CONTINUE')
      } else {
        onClose()
      }
    }
  }, [state, showSuccessModal, onClose, send])

  if (!workspace) return null

  const isCancelFlow = state.matches('provideReason') || state.matches('cancelSuccess')

  const openAutopilotUpgradeModal = () => {
    openModal(
      AutopilotUpgradeModal,
      { isCancellationFlow: true },
      {
        underlayVariant: isMobile ? 'mobileAutopilotUpgrade' : 'autopilotUpgrade',
      },
    )
  }

  if (state.matches('success')) {
    let message = "Success — you've upgraded your plan!"
    if (selectedPlan?.name) {
      message = `Success — you’re now on the ${selectedPlan.name} plan! `
    }
    if (onUpgradePlanSuccess) {
      onUpgradePlanSuccess(message)
    } else {
      onClose()
    }
    return null
  } else {
    return (
      <Modal.Root
        onPressOutside={onClose}
        onClose={onClose}
        size="md"
        bodyClassName={cx({ [Styles.cancelFlow]: isCancelFlow })}
      >
        {state.matches('selectPlan') && (
          <SelectPlan
            availablePlans={paymentPlans}
            billingFrequency={billingFrequency}
            onClose={onClose}
            savedPlan={savedPlan}
            selectPlan={selectPlan}
            setBillingFrequency={setBillingFrequency}
            showWorkspacesLink={showWorkspacesLink}
            isCanceled={isCanceled}
          />
        )}
        {state.matches('provideReason') && (
          <ProvideReason
            onCancel={onClose}
            onNext={(periodEnd) => {
              if (!workspace.is_virtual_marketer) {
                openAutopilotUpgradeModal()
              }
              setSubscriptionPeriodEndsAt(periodEnd)
              send('CANCEL_SUCCESS')
            }}
          />
        )}
        {state.matches('cancelSuccess') && subscriptionPeriodEndsAt && (
          <CancelSuccess onClose={onClose} periodEnd={subscriptionPeriodEndsAt} />
        )}
        {(state.matches('payment') || state.matches('paymentSuccess')) && selectedPlan && (
          <PaymentForm
            onClose={onClose}
            onSuccess={onSuccess || updateWorkspacePaying}
            selectedPlan={selectedPlan}
            savedPlan={savedPlan}
            billingFrequency={billingFrequency}
            setBillingFrequency={setBillingFrequency}
            workspace={workspace}
            send={send}
            startDate={startDate()}
            isCanceled={isCanceled}
          />
        )}
      </Modal.Root>
    )
  }
}
